import React, { useState } from 'react';
import { B } from '../constants';
import { borderRadius, shadows } from '../designTokens';
import Button from '../components/ui/Button';
import Checkbox from '../components/ui/Checkbox';
import Input from '../components/ui/Input';
import { trips, maps as mapsApi } from '../api';
import { geocodeWithCache } from '../lib/geocode';
import DestinationsMap from '../components/DestinationsMap';
import AIItineraryModal from '../components/AIItineraryModal';
import TemplateSelectorModal from '../components/TemplateSelectorModal';
import FlightSearchModal from '../components/FlightSearchModal';
import FlightWidget from '../components/FlightWidget';

export default function TripPlannerScreen({ goScreen, showToast, initialData }) {
  const [title, setTitle] = useState('');
  const [destination, setDestination] = useState(initialData?.destination || '');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [tripType] = useState(initialData?.type || 'new'); // 'new' or 'booked'
  const [days, setDays] = useState([{ dayNumber: 1, title: '', date: '', activities: [] }]);
  const [saving, setSaving] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showFlightSearch, setShowFlightSearch] = useState(false);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [optimizingDay, setOptimizingDay] = useState(null);
  
  // Budget breakdown
  const [budgetBreakdown, setBudgetBreakdown] = useState({
    flights: 0,
    accommodation: 0,
    food: 0,
    transport: 0,
    activities: 0,
    misc: 0
  });
  
  const totalBudget = Object.values(budgetBreakdown).reduce((a, b) => a + b, 0);

  const handleSelectFlight = (offer) => {
    setSelectedFlight(offer);
    const flightCost = Math.round(Number(offer.price?.total || 0));
    if (flightCost > 0) {
      setBudgetBreakdown((prev) => ({ ...prev, flights: flightCost }));
    }
    showToast?.('Flight added to your trip ✈️');
  };

  const addDay = () => {
    setDays([...days, { dayNumber: days.length + 1, title: '', date: '', activities: [] }]);
  };

  const removeDay = (idx) => {
    setDays(days.filter((_, i) => i !== idx).map((d, i) => ({ ...d, dayNumber: i + 1 })));
  };

  const updateDay = (idx, field, value) => {
    const newDays = [...days];
    newDays[idx] = { ...newDays[idx], [field]: value };
    setDays(newDays);
  };

  const addActivity = (dayIdx) => {
    const newDays = [...days];
    newDays[dayIdx].activities = [...newDays[dayIdx].activities, { name: '', time: '', location: '', notes: '' }];
    setDays(newDays);
  };

  const updateActivity = (dayIdx, actIdx, field, value) => {
    const newDays = [...days];
    newDays[dayIdx].activities[actIdx] = { ...newDays[dayIdx].activities[actIdx], [field]: value };
    setDays(newDays);
  };

  const removeActivity = (dayIdx, actIdx) => {
    const newDays = [...days];
    newDays[dayIdx].activities = newDays[dayIdx].activities.filter((_, i) => i !== actIdx);
    setDays(newDays);
  };

  // Reorder a day's activities into an optimized visiting sequence. The trip
  // isn't saved yet, so we geocode locally (backend Google geocoder when
  // configured, OpenStreetMap fallback) and call the stateless /maps/optimize
  // endpoint, then rewrite the day's activity order in place.
  const handleOptimizeDay = async (dayIdx) => {
    const day = days[dayIdx];
    const located = day.activities
      .map((act, actIdx) => ({ act, actIdx }))
      .filter(({ act }) => (act.location || act.name || '').trim());
    if (located.length < 2) {
      showToast?.('Add at least two activities with locations to optimize.', 'error');
      return;
    }
    setOptimizingDay(dayIdx);
    try {
      let geo = { results: [] };
      try {
        geo = await mapsApi.geocode(
          located.map(({ act, actIdx }) => ({ id: actIdx, name: act.name, location: act.location })),
          { context: destination },
        );
      } catch { /* fall back to client geocoder below */ }

      const points = [];
      for (const { act, actIdx } of located) {
        const hit = (geo.results || []).find((r) => r.id === actIdx);
        let lat = hit?.lat;
        let lng = hit?.lng;
        if (typeof lat !== 'number' || typeof lng !== 'number') {
          // eslint-disable-next-line no-await-in-loop
          const c = await geocodeWithCache(act.location || act.name, destination);
          if (c) { lat = c.lat; lng = c.lng; }
        }
        if (typeof lat === 'number' && typeof lng === 'number') {
          points.push({ id: actIdx, lat, lng });
        }
      }

      if (points.length < 2) {
        showToast?.('Could not locate enough activities to optimize.', 'error');
        return;
      }

      const res = await mapsApi.optimize(points);
      const orderedActIdx = res.order.map((i) => points[i].id);
      const orderedActs = orderedActIdx.map((i) => day.activities[i]);
      const remaining = day.activities.filter((_, i) => !orderedActIdx.includes(i));
      const newDays = [...days];
      newDays[dayIdx] = { ...day, activities: [...orderedActs, ...remaining] };
      setDays(newDays);

      const km = res.totalDistanceMeters ? (res.totalDistanceMeters / 1000).toFixed(1) : null;
      const by = res.provider === 'google' ? 'Google Maps' : 'distance';
      showToast?.(`Day ${day.dayNumber} route optimized by ${by}${km ? ` · ${km} km` : ''} 🗺️`);
    } catch (err) {
      showToast?.(err.message || 'Failed to optimize route', 'error');
    } finally {
      setOptimizingDay(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // First validate required fields
    if (!title || !destination) {
      showToast('Title and destination are required', 'error');
      return;
    }
    if (!startDate || !endDate) {
      showToast('Start date and end date are required', 'error');
      return;
    }
    if (new Date(endDate) < new Date(startDate)) {
      showToast('End date must be on or after start date', 'error');
      return;
    }
    
    // Check at least one day has content
    const validDays = days.filter(d => d.title || d.activities.length > 0);
    if (validDays.length === 0) {
      showToast('Please add at least one day with title or activities', 'error');
      return;
    }
    
    setSaving(true);
    try {
      const trip = await trips.create({
        title, destination, startDate, endDate, budget: totalBudget || null, notes, isPublic,
        days: validDays,
      });
      showToast(tripType === 'booked' ? 'Trip added to your experiences!' : 'Trip created successfully!');
      goScreen('tripDetail', trip);
    } catch (err) {
      showToast(err.message || 'Failed to create trip', 'error');
    } finally {
      setSaving(false);
    }
  };

  const updateBudget = (field, value) => {
    setBudgetBreakdown(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 20px' }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <button onClick={() => goScreen('userJourney')} aria-label="Back to journey" style={{ background: 'none', border: 'none', cursor: 'pointer', color: B.forest, fontSize: 24 }}>
            ←
          </button>
          <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 32, color: B.night, margin: 0 }}>
            {tripType === 'booked' ? 'Share Your Experience 📋' : 'Plan Your Trip ✈️'}
          </h1>
        </div>
        <p style={{ color: B.muted }}>
          {tripType === 'booked' 
            ? 'Share your travel experience and help others plan their trips'
            : 'Create a detailed itinerary for your adventure'
          }
        </p>
        <button 
          type="button"
          onClick={() => setShowAIModal(true)}
          style={{
            background: B.amber,
            color: B.night,
            border: 'none',
            padding: '10px 20px',
            borderRadius: 20,
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: 13,
            marginBottom: 24
          }}
        >
          ✨ AI Generate Itinerary
        </button>
        <button 
          type="button"
          onClick={() => setShowTemplates(true)}
          style={{
            background: B.sky,
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: 20,
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: 13,
            marginLeft: 12,
            marginBottom: 24
          }}
        >
          📋 Use Template
        </button>
        <Button
          variant="primary" size="sm"
          onClick={() => setShowFlightSearch(true)}
          style={{ marginLeft: 12, marginBottom: 24 }}
        >
          ✈️ Add Flights
        </Button>
      </div>

      {destination && startDate && (
        <FlightWidget
          trip={{ destination, startDate, endDate }}
          onOpenFlightSearch={() => setShowFlightSearch(true)}
        />
      )}

      {selectedFlight && (
        <div
          style={{
            background: 'white',
            border: `1px solid ${B.forest}`,
            borderRadius: 12,
            padding: 16,
            marginBottom: 24,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div style={{ fontWeight: 700, color: B.night }}>
              ✈️ {selectedFlight.itineraries?.[0]?.segments?.[0]?.departure?.iataCode} →{' '}
              {
                selectedFlight.itineraries?.[0]?.segments?.[
                  selectedFlight.itineraries[0].segments.length - 1
                ]?.arrival?.iataCode
              }
              {selectedFlight.itineraries?.[1] && ' (round trip)'}
            </div>
            <div style={{ fontSize: 13, color: B.muted, marginTop: 2 }}>
              {selectedFlight.price?.currency} {Number(selectedFlight.price?.total || 0).toFixed(2)} •{' '}
              {selectedFlight.itineraries?.[0]?.segments?.[0]?.carrierName ||
                selectedFlight.itineraries?.[0]?.segments?.[0]?.carrierCode}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setSelectedFlight(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#dc3545',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Remove flight
          </button>
        </div>
      )}

      {/* Trip Type Badge */}
      <div style={{ marginBottom: 24 }}>
        <span style={{
          background: tripType === 'booked' ? 'rgba(245,166,35,0.1)' : 'rgba(26,107,74,0.1)',
          color: tripType === 'booked' ? B.amber : B.forest,
          padding: '8px 16px',
          borderRadius: 20,
          fontSize: 13,
          fontWeight: 700,
          fontFamily: "'Montserrat',sans-serif"
        }}>
          {tripType === 'booked' ? '📋 Already Booked' : '✈️ Planning New Trip'}
        </span>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Basic Info */}
        <div style={{ background: 'white', borderRadius: borderRadius.lg, padding: 28, marginBottom: 24, boxShadow: shadows.md }}>
          <h3 style={{ marginBottom: 20, fontFamily: "'Montserrat',sans-serif", fontWeight: 700 }}>Basic Info</h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13, color: B.night }}>Trip Title *</label>
              <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g., Summer in Italy" style={{ background: '#fafafa' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13, color: B.night }}>Destination *</label>
              <Input value={destination} onChange={e => setDestination(e.target.value)} placeholder="e.g., Rome, Italy" style={{ background: '#fafafa' }} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13, color: B.night }}>Start Date</label>
              <Input type="date" min={new Date().toISOString().split('T')[0]} value={startDate} onChange={e => setStartDate(e.target.value)} style={{ background: '#fafafa' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13, color: B.night }}>End Date</label>
              <Input type="date" min={startDate || new Date().toISOString().split('T')[0]} value={endDate} onChange={e => setEndDate(e.target.value)} style={{ background: '#fafafa' }} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13, color: B.night }}>Notes</label>
              <textarea style={{ width: '100%', padding: '12px 16px', borderRadius: borderRadius.md, border: '1px solid #ddd', fontSize: 14, fontFamily: "'Montserrat',sans-serif", background: '#fafafa', boxSizing: 'border-box', minHeight: 80, resize: 'vertical' }} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any additional notes..." />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', paddingTop: 28 }}>
              <Checkbox
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                label="Make this trip public"
                size="sm"
              />
            </div>
          </div>
        </div>

        {/* Budget Calculator */}
        <div style={{ background: 'white', borderRadius: borderRadius.lg, padding: 28, marginBottom: 24, boxShadow: shadows.md }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ fontFamily: "'Montserrat',sans-serif", fontWeight: 700, margin: 0 }}>💰 Budget Calculator</h3>
            <div style={{ 
              background: `linear-gradient(135deg,${B.forest},${B.mint})`,
              color: 'white',
              padding: '12px 24px',
              borderRadius: 12,
              fontFamily: "'Montserrat',sans-serif",
              fontWeight: 700
            }}>
              Total: ${totalBudget.toLocaleString()}
            </div>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
            {[
              { key: 'flights', label: '✈️ Flights', icon: '✈️' },
              { key: 'accommodation', label: '🏨 Accommodation', icon: '🏨' },
              { key: 'food', label: '🍽️ Food', icon: '🍽️' },
              { key: 'transport', label: '🚗 Transport', icon: '🚗' },
              { key: 'activities', label: '🎯 Activities', icon: '🎯' },
              { key: 'misc', label: '📦 Misc', icon: '📦' },
            ].map(item => (
              <div key={item.key}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 12, color: B.night }}>{item.label}</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: B.muted, zIndex: 1 }}>$</span>
                  <Input type="number" value={budgetBreakdown[item.key] || ''} onChange={e => updateBudget(item.key, e.target.value)} placeholder="0" style={{ paddingLeft: 28, background: '#fafafa' }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Itinerary */}
        <div style={{ background: 'white', borderRadius: borderRadius.lg, padding: 28, marginBottom: 24, boxShadow: shadows.md }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
            <h3 style={{ fontFamily: "'Montserrat',sans-serif", fontWeight: 700, margin: 0 }}>📅 Itinerary</h3>
            <Button variant="primary" size="sm" onClick={addDay}>+ Add Day</Button>
          </div>

          {days.map((day, dayIdx) => (
            <div key={dayIdx} style={{ border: '1px solid #eee', borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontWeight: 700, color: B.forest }}>Day {day.dayNumber}</span>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  {day.activities.length >= 2 && (
                    <button
                      type="button"
                      disabled={optimizingDay === dayIdx}
                      onClick={() => handleOptimizeDay(dayIdx)}
                      style={{ background: 'none', border: 'none', color: optimizingDay === dayIdx ? B.muted : B.forest, cursor: optimizingDay === dayIdx ? 'default' : 'pointer', fontSize: 13, fontWeight: 600 }}
                    >
                      {optimizingDay === dayIdx ? 'Optimizing…' : '🗺️ Optimize route'}
                    </button>
                  )}
                  {days.length > 1 && (
                    <button type="button" onClick={() => removeDay(dayIdx)} style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: 13 }}>
                      Remove
                    </button>
                  )}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 12 }}>
                <Input value={day.title} onChange={e => updateDay(dayIdx, 'title', e.target.value)} placeholder="Day title (e.g., Arrival & Check-in)" style={{ background: '#fafafa' }} />
                <Input type="date" value={day.date} onChange={e => updateDay(dayIdx, 'date', e.target.value)} style={{ background: '#fafafa' }} />
              </div>

              {day.activities.map((act, actIdx) => (
                <div key={actIdx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'start', flexWrap: 'wrap' }}>
                  <Input value={act.time} onChange={e => updateActivity(dayIdx, actIdx, 'time', e.target.value)} placeholder="Time" style={{ width: 80, flexShrink: 0, background: '#fafafa' }} />
                  <Input value={act.name} onChange={e => updateActivity(dayIdx, actIdx, 'name', e.target.value)} placeholder="Activity" style={{ flex: 1, minWidth: 100, background: '#fafafa' }} />
                  <Input value={act.location} onChange={e => updateActivity(dayIdx, actIdx, 'location', e.target.value)} placeholder="Location" style={{ flex: 1, minWidth: 100, background: '#fafafa' }} />
                  <button type="button" onClick={() => removeActivity(dayIdx, actIdx)} style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: 18, padding: '8px' }}>×</button>
                </div>
              ))}

              <button type="button" onClick={() => addActivity(dayIdx)} style={{ background: 'none', border: '1px dashed #ccc', color: B.muted, width: '100%', padding: 8, borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
                + Add Activity
              </button>
            </div>
          ))}
        </div>

        {/* Map preview of destination + activity locations */}
        <div style={{ background: 'white', borderRadius: borderRadius.lg, padding: 28, marginBottom: 24, boxShadow: shadows.md }}>
          <h3 style={{ fontFamily: "'Montserrat',sans-serif", fontWeight: 700, marginTop: 0, marginBottom: 20 }}>🗺️ Map</h3>
          <DestinationsMap
            height={360}
            emptyLabel="Add a destination or activity locations to see them plotted here."
            destinations={[
              ...(destination ? [{ id: 'planner-destination', name: destination, description: title || 'Destination' }] : []),
              ...days.flatMap((day, dayIdx) =>
                day.activities
                  .filter((act) => act.location)
                  .map((act, actIdx) => ({
                    id: `planner-act-${dayIdx}-${actIdx}`,
                    name: act.location,
                    description: act.name || `Day ${day.dayNumber}`,
                  }))
              ),
            ]}
          />
        </div>

        <Button
          variant="primary" size="lg"
          loading={saving}
          style={{ width: '100%', borderRadius: borderRadius.md, fontSize: 16 }}
        >
          {tripType === 'booked' ? 'Share Experience' : 'Create Trip'}
        </Button>
        </form>
        {showAIModal && (
          <AIItineraryModal 
            onClose={() => setShowAIModal(false)}
            onImport={(days, title) => {
              setDays(days);
              if (title) setTitle(title);
            }}
          />
        )}
        {showTemplates && (
          <TemplateSelectorModal
            onClose={() => setShowTemplates(false)}
            onSelect={(template) => {
              setDays(template.days.map(d => ({ ...d, activities: [] })));
              setShowTemplates(false);
            }}
          />
        )}
        {showFlightSearch && (
          <FlightSearchModal
            onClose={() => setShowFlightSearch(false)}
            initialDepartureDate={startDate}
            initialReturnDate={endDate}
            selectedOfferId={selectedFlight?.id}
            onSelectFlight={(offer) => {
              handleSelectFlight(offer);
            }}
          />
        )}
      </div>
    );
  }
